import { useEffect } from "react";

import { faCircleNotch } from "@fortawesome/pro-regular-svg-icons";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useDebounceValue } from "usehooks-ts";

import { Schema } from "src/api/flowTypes";
import { PillToggle } from "src/base-components/PillToggle";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import {
  areSchemasEnabled,
  FEATURES_PROPERTY,
  schemaContainsCase,
  schemaContainsEntity,
  schemaContainsEvent,
  schemaContainsFeatures,
} from "src/schema/schemaEditor/utils";
import { SchemaUI } from "src/schema/schemaMappingUtils";

export enum SchemaViewType {
  EVENT = "event",
  ENTITY = "entity",
  CUSTOM = "custom",
  CASE = "case",
}

type SchemaTogglesProps = {
  isReadonly: boolean;
  activeView: SchemaViewType;
  setActiveView: (view: SchemaViewType) => void;
  formMethods: UseFormReturn<SchemaUI>;
  isOutputSchemaConfigured?: boolean;
  isSchemaMutating?: boolean;
  schema: Schema;
};

const useDebouncedMutationState = (isPending: boolean) => {
  // Show mutation state only if it hasn't finished in 300ms
  const [isDebouncedPending, setIsDebouncedPending] = useDebounceValue(
    isPending,
    300,
  );

  useEffect(() => {
    setIsDebouncedPending(isPending);

    if (!isPending) {
      // Update the value immediately if it is not pending
      setIsDebouncedPending.flush();
    }
  }, [isPending, setIsDebouncedPending]);

  return isDebouncedPending;
};

export const SchemaToggles = ({
  isReadonly,
  activeView,
  setActiveView,
  formMethods,
  isOutputSchemaConfigured = false,
  isSchemaMutating = false,
  schema,
}: SchemaTogglesProps) => {
  const featureGates = useWorkspaceFeatureGates();
  const { isEnabled: isCaseManagementModuleEnabled } =
    useIsCaseManagementEnabled();
  const isDebouncedSchemaMutating = useDebouncedMutationState(isSchemaMutating);

  const formProperties = useWatch({
    control: formMethods.control,
    name: "properties",
  });

  const containsEvent = schemaContainsEvent(formProperties);
  const containsEntity = schemaContainsEntity(formProperties);
  const containsCase = schemaContainsCase(formProperties, schema);
  const containsFeatures = schemaContainsFeatures(formProperties);

  const {
    eventSchemaEnabled,
    entitySchemaEnabled,
    customSchemaEnabled,
    caseSchemaEnabled,
  } = areSchemasEnabled(formProperties, isOutputSchemaConfigured, schema);

  const addGlobalFeatures = () => {
    if (!containsFeatures && (containsEvent || containsEntity)) {
      formMethods.setValue("properties", [
        ...formMethods.getValues("properties"),
        FEATURES_PROPERTY,
      ]);
    }
  };

  return (
    <div className="mb-4 flex items-center justify-between">
      <PillToggle value={activeView} onChange={setActiveView}>
        <Tooltip
          disabled={customSchemaEnabled}
          title="Existing fields should be deleted to change the schema type."
          asChild
        >
          <PillToggle.Button
            disabled={isReadonly || !customSchemaEnabled || isSchemaMutating}
            selected={activeView === SchemaViewType.CUSTOM}
            value={SchemaViewType.CUSTOM}
            onChange={() => setActiveView(SchemaViewType.CUSTOM)}
          >
            Custom
          </PillToggle.Button>
        </Tooltip>
        <>
          {featureGates.featuresEventsEnabled && (
            <Tooltip
              disabled={eventSchemaEnabled}
              title={
                containsCase
                  ? "Existing fields should be deleted to change the schema type."
                  : "Input schema must contain only a single Event."
              }
              asChild
            >
              <PillToggle.Button
                disabled={isReadonly || !eventSchemaEnabled || isSchemaMutating}
                selected={activeView === SchemaViewType.EVENT}
                value={SchemaViewType.EVENT}
                onChange={() => {
                  setActiveView(SchemaViewType.EVENT);
                  addGlobalFeatures();
                }}
              >
                Event
              </PillToggle.Button>
            </Tooltip>
          )}
        </>
        <>
          {featureGates.entitiesEnabled && (
            <Tooltip
              disabled={entitySchemaEnabled}
              title={
                containsCase
                  ? "Existing fields should be deleted to change the schema type."
                  : "Input schema must contain only a single Entity."
              }
              asChild
            >
              <PillToggle.Button
                disabled={
                  isReadonly || !entitySchemaEnabled || isSchemaMutating
                }
                selected={activeView === SchemaViewType.ENTITY}
                value={SchemaViewType.ENTITY}
                onChange={() => {
                  setActiveView(SchemaViewType.ENTITY);
                  addGlobalFeatures();
                }}
              >
                Entity
              </PillToggle.Button>
            </Tooltip>
          )}
        </>
        <>
          {isCaseManagementModuleEnabled && (
            <Tooltip
              disabled={caseSchemaEnabled}
              title="Existing fields should be deleted to change the schema type."
              asChild
            >
              <PillToggle.Button
                disabled={isReadonly || !caseSchemaEnabled || isSchemaMutating}
                selected={activeView === SchemaViewType.CASE}
                value={SchemaViewType.CASE}
                onChange={() => setActiveView(SchemaViewType.CASE)}
              >
                Case
              </PillToggle.Button>
            </Tooltip>
          )}
        </>
      </PillToggle>
      {isDebouncedSchemaMutating && (
        <Tooltip
          body="Schema updating took longer than expected"
          title="Schema updating in progress"
          asChild
        >
          <Icon icon={faCircleNotch} size="sm" spin />
        </Tooltip>
      )}
    </div>
  );
};
